import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';
import type { Article } from '@/mocks/seo-content';

interface ArticleContentProps {
  article: Article;
}

export default function ArticleContent({ article }: ArticleContentProps) {
  return (
    <article className="max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-16">
      <nav className="mb-8" aria-label="Breadcrumb">
        <ol className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
          <li>
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">
              Home
            </Link>
          </li>
          <li>
            <i className="ri-arrow-right-s-line text-xs"></i>
          </li>
          <li>
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">
              Guides
            </Link>
          </li>
          <li>
            <i className="ri-arrow-right-s-line text-xs"></i>
          </li>
          <li>
            <span className="text-foreground-800 font-medium whitespace-nowrap line-clamp-1">
              {article.title}
            </span>
          </li>
        </ol>
      </nav>

      <header className="mb-10">
        <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 leading-tight mb-6">
          {article.title}
        </h1>

        <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
          <span className="whitespace-nowrap">
            <i className="ri-calendar-line mr-1.5"></i>
            Published: {article.datePublished}
          </span>
          <span className="whitespace-nowrap">
            <i className="ri-time-line mr-1.5"></i>
            {article.readTime}
          </span>
          <span className="whitespace-nowrap">
            <i className="ri-file-text-line mr-1.5"></i>
            {article.sections.length} sections
          </span>
        </div>

        <div className="flex flex-wrap gap-2">
          {['Sugargoo Spreadsheet', 'Shopping Guide', 'Product Database', 'QC Guide', 'Shipping Tips', '2026'].map((tag) => (
            <span
              key={tag}
              className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap"
            >
              {tag}
            </span>
          ))}
        </div>
      </header>

      <div className="prose-custom">
        {article.sections.map((section, idx) => (
          <section key={idx} className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
              {section.heading}
            </h2>
            {section.content.split('\n\n').map((paragraph, pIdx) => (
              <p
                key={pIdx}
                className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4"
              >
                {paragraph}
              </p>
            ))}
          </section>
        ))}
      </div>

      <div className="mt-12 p-6 md:p-8 rounded-lg bg-primary-50 border border-primary-200/70">
        <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-3">
          Ready to explore the Sugargoo Spreadsheet?
        </h3>
        <p className="text-foreground-600 text-sm mb-5">
          Browse our organized category database and discover thousands of curated product finds across Shoes, Hoodies, Bags, Accessories and more.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/"
            className="inline-flex items-center px-5 py-2.5 text-sm font-semibold rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-home-line mr-2"></i>
            Back to Homepage
          </Link>
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center px-5 py-2.5 text-sm font-semibold rounded-md bg-background-50 text-foreground-950 border border-background-200/70 hover:bg-background-100 transition-colors whitespace-nowrap"
          >
            <i className="ri-external-link-line mr-2"></i>
            Visit Main Platform
          </a>
        </div>
      </div>

      <div className="mt-10 pt-8 border-t border-background-200/70">
        <h4 className="font-heading text-base font-semibold text-foreground-950 mb-4">
          Related Categories
        </h4>
        <div className="flex flex-wrap gap-2">
          {categories.slice(0, 8).map((cat) => (
            <a
              key={cat.slug}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 rounded-md bg-background-100 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap border border-background-200/70"
            >
              {cat.name}
            </a>
          ))}
        </div>
      </div>
    </article>
  );
}